/**
 * Category Hooks
 */
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { categoryService } from '../services/categoryService';
import { queryTags } from '../queryTags';
import { getQueryClient } from '../queryClient';
import type {
  CreateCategoryPayload,
  UpdateCategoryPayload,
  GetCategoriesParams,
} from '../types/category.types';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';

/**
 * Get categories/subcategories hook
 */
export function useGetCategories(
  params?: GetCategoriesParams,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [
      queryTags.category,
      params?.parentId || 'all',
      params?.search || '',
      params?.page || 1,
      params?.limit || 10,
    ],
    queryFn: () => categoryService.getCategories(params),
    ...options,
  });
}

/**
 * Get single category/subcategory hook
 */
export function useGetCategory(
  id: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.category, id],
    queryFn: () => categoryService.getCategory(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Create category/subcategory mutation hook
 */
export function useCreateCategory(
  options?: UseMutationOptions<any, any, CreateCategoryPayload, any>
) {
  return useApiMutation<any, any, CreateCategoryPayload, any>({
    mutationFn: ((data: CreateCategoryPayload) => categoryService.createCategory(data)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.category] });
    },
    ...options,
  });
}

/**
 * Update category/subcategory mutation hook
 */
export function useUpdateCategory(
  options?: UseMutationOptions<any, any, { id: string; data: UpdateCategoryPayload }, any>
) {
  return useApiMutation<any, any, { id: string; data: UpdateCategoryPayload }, any>({
    mutationFn: ((variables: { id: string; data: UpdateCategoryPayload }) =>
      categoryService.updateCategory(variables.id, variables.data)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.category] });
    },
    ...options,
  });
}

/**
 * Delete category/subcategory mutation hook
 */
export function useDeleteCategory(
  options?: UseMutationOptions<any, any, string, any>
) {
  return useApiMutation<any, any, string, any>({
    mutationFn: ((id: string) => categoryService.deleteCategory(id)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.category] });
    },
    ...options,
  });
}

